import type { RouteComponentProps } from 'react-router-dom';
import type {
  ThunkAction,
  ThunkExtras,
  GetState,
  APIError,
  Dispatch,
  AppState,
  AppAction,
} from '../../types/app.types';
import type {
  V2Response,
  V2Account,
  V2UserSummary,
  AccountAddress,
  V2Prescription,
  MagicCouponType,
  V2AccountStats,
} from '../../types/api-v2.types';
import { setExperiments } from './ab-module';
import { addressIsDeliverySafe } from '../../utils/states';
import { requestCart, hasAllRefills as getHasAllRefills, getAutoRefillEligibleMeds } from './cart-module';
import { getScheduledMedNameIds } from './recurring-schedule-module';
import { trackEvent, identify } from '../../utils/analytics';
import * as querystring from 'query-string';
import { isEligible } from './subscriptions-module';
import { isDesktop } from './header-module';
import { updateDeliveryAddress } from './delivery-module';
import { hasTelemedDrugs } from './telemed-module';
import {
  REFERRAL_FIRST_FILL_COUPON_MESSAGE,
  NON_REFERRAL_FIRST_FILL_COUPON_MESSAGE,
  PRIVIA_BANNER_MESSAGE,
  IS_BROWSER,
} from '../../config';
import { getDrug } from './drugs-module';
import type { Drug } from './drugs.types';
import { getHasMagicAccess, requestMagicTierAccess } from './available-pricing-tiers-module';
import isOpioid from '../../utils/is-opioid';
import {
  CHECKOUT_STEPS,
  CHECKOUT_SUBSCRIPTIONS_BASE_PATH,
  CHECKOUT_ROUTES_DELIVERY,
  CHECKOUT_ROUTES_PICKUP,
  CHECKOUT_ROUTES_SUBSCRIPTION,
} from '../../views/checkout/config';
import { getUserMagicTier } from './available-pricing-tiers-module';

const MAGIC_CLAIM_RIGID_MATCH_FIELD_LIST = ['firstName', 'lastName', 'dob', 'mobileNumber'];
// Last UserLocation is stored in local storage:
const USER_LOCATION_SESSION_STORAGE_KEY = 'last_user_location_set';
export const getUserLocationFromSessionStorage = () => {
  try {
    const storedLocation = sessionStorage && sessionStorage.getItem(USER_LOCATION_SESSION_STORAGE_KEY);
    return storedLocation && JSON.parse(storedLocation);
  } catch (e) {
    console.error(e, 'sessionStorage not supported');
    return null;
  }
};
export const setUserLocationToSessionStorage = ({ location }: { location: UserLocationType }) => {
  try {
    sessionStorage && sessionStorage.setItem(USER_LOCATION_SESSION_STORAGE_KEY, JSON.stringify(location));
  } catch (e) {
    // eslint-disable-next-line
    console.error(e, 'sessionStorage not supported');
    return null;
  }
};

export function handleLocation({ dispatch, isLoggedIn }: { dispatch: Dispatch; isLoggedIn: boolean }) {
  // Try to read last location set from local storage
  const lastUserLocationSet = getUserLocationFromSessionStorage();

  if (lastUserLocationSet) {
    dispatch(setUserLocation(lastUserLocationSet));
    return;
  }

  // If no prevous location set, detect user's location:
  // If logged in and not magic card, get location from account address
  if (isLoggedIn) {
    dispatch(getUserLocation());
  } else {
    // If not logged in, try to infer user's location
    dispatch(inferUserLocation());
  }
}

/**
 * types
 */
export type UpdateAccountFields = {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: 'male' | 'female';
  smsOptIn?: boolean;
  mobileNumber?: string;
  medInfoOptIn?: boolean;
  hasShippingAddress?: boolean;
  email?: string;
};

export type UserLocationType = {
  city: string | null | undefined;
  stateCode: string | null | undefined;
  zipCode: string | null | undefined;
  healthSystems: Array<string> | null | undefined;
  error?: string;
};
export type AccountState = {
  dob: string | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  gender: 'male' | 'female';
  id: string | null | undefined;
  lastName: string | null | undefined;
  mobileNumber: string | null | undefined;
  orderCount: number;
  sessionId: string | null | undefined;
  type: ('user' | null | undefined) | 'guest' | 'sms_otp' | 'new';
  prescriptions: Array<V2Prescription> | null | undefined;
  paymentCardCount: number;
  hasShippingAddress: boolean | null | undefined;
  hasPatientProfile: boolean;
  smsOptIn: boolean;
  medInfoOptIn: boolean;
  hasSubscriptions: boolean;
  hasConsultations: boolean;
  hasReferralCoupon: boolean;
  hasMagicCoupon: boolean;
  isReverieUser: boolean;
  isMagicVerified: boolean;
  telemedCurrentState: string | null | undefined;
  userLocation?: UserLocationType | null | undefined;
  magicCoupon: MagicCouponType | null | undefined;
};
// All fields are optional in AccountStateUpdate
export type AccountStateUpdate = {
  dob?: string;
  email?: string;
  firstName?: string;
  gender?: 'male' | 'female';
  id?: string;
  lastName?: string;
  mobileNumber?: string;
  orderCount?: number;
  sessionId?: string;
  type?: 'user' | 'guest';
  prescriptions?: Array<V2Prescription>;
  hasShippingAddress?: boolean;
};
export type MaybeAccountAddress = {
  address1?: string;
  address2?: string | null | undefined;
  city?: string;
  state?: string;
  zipCode?: string;
  isVerified?: boolean;
  source?: string;
  specialInstructions?: string | null | undefined;
};
export type MatchMagicFields = {
  lastName: string;
  dob: string;
  matchCode: string | null | undefined;
  mobileNumber: string | null | undefined;
};
export type AccountType = 'new' | 'guest' | 'user';
export type GetAccountSummary = {
  type: 'account/GET_SUMMARY';
};
export type GetAccountSummarySuccess = {
  type: 'account/GET_SUMMARY_SUCCESS';
  data: V2UserSummary;
};
export type ReceiveMagicCoupon = {
  type: 'account/RECEIVE_MAGIC_COUPON';
  magicCoupon: MagicCouponType;
};
export type GetAccount = {
  type: 'account/GET';
};
export type GetAccountSuccess = {
  type: 'account/GET_SUCCESS';
  data: V2Account;
};
export type GetAccountError = {
  type: 'account/GET_ERROR';
} & APIError;
export type GetAccountAddressSuccess = {
  type: 'account/GET_ADDRESS_SUCCESS';
  data: AccountAddress;
};
export type GetAccountAddressError = {
  type: 'account/GET_ADDRESS_ERROR';
} & APIError;
export type LogIn = {
  type: 'account/LOG_IN';
};
export type LogInError = {
  type: 'account/LOG_IN_ERROR';
} & APIError;
export type Register = {
  type: 'account/REGISTER';
};
export type RegisterError = {
  type: 'account/REGISTER_ERROR';
} & APIError;
export type Update = {
  type: 'account/UPDATE';
};
export type UpdateError = {
  type: 'account/UPDATE_ERROR';
} & APIError;
export type LogOut = {
  type: 'account/LOG_OUT';
};
export type LogOutError = {
  type: 'account/LOG_OUT_ERROR';
};
export type CreatePassword = {
  type: 'account/CREATE_PASSWORD';
};
export type ChangePasswordSuccess = {
  type: 'account/CHANGE_PASSWORD_SUCCESS';
};
export type ChangePasswordError = {
  type: 'account/CHANGE_PASSWORD_ERROR';
} & APIError;
export type GuestLogin = {
  type: 'account/GUEST_LOGIN';
};
export type UpdateAccountState = {
  type: 'account/UPDATE_STATE';
  state: AccountStateUpdate;
};
export type SetUserCurrentState = {
  type: 'account/SET_USER_CURRENT_STATE';
  data: string | null | undefined;
};
export type MarkReverieUserSuccess = {
  type: 'account/MARK_REVERIE_USER_SUCCESS';
};
export type SetUserLocationSuccess = {
  type: 'user/SET_USER_LOCATION_SUCCESS';
  data: UserLocationType;
};
export type AccountAction =
  | UpdateAccountState
  | ChangePasswordSuccess
  | ChangePasswordError
  | CreatePassword
  | GuestLogin
  | GetAccountSummary
  | GetAccountSummarySuccess
  | ReceiveMagicCoupon
  | GetAccountAddressSuccess
  | GetAccountAddressError
  | GetAccount
  | GetAccountSuccess
  | GetAccountError
  | LogIn
  | LogInError
  | LogOut
  | LogOutError
  | Register
  | RegisterError
  | Update
  | UpdateError
  | MarkReverieUserSuccess
  | SetUserCurrentState
  | SetUserLocationSuccess;

/**
 * Actions
 */
export function getAccountSummary(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    const qsParams = extras.req ? extras.req.query : querystring.parse(window.location.search);
    return extras.blinkAPIClient
      .get('/v2/user', {
        params: {
          xp: qsParams.xp || undefined, // Allow forcing A/B variation
        },
      })
      .then((res) => res.data)
      .then((data: V2Response<V2UserSummary>) => data.result)
      .then((data) => {
        const promises = [];

        // South Dakota
        if (!data.isServiceAvailable) {
          extras.history.replace({
            pathname: '/mobile/unavailable',
          });
        }

        // If we have meds in cart get the cart
        if (data.stats && data.stats.cartItemCount) {
          promises.push(dispatch(requestCart()));
        }

        if (data.experiments != null && data.experiments.length > 0) {
          promises.push(dispatch(setExperiments(data.experiments)));
        }

        if (data.account && data.account.id) {
          identify(data.account.id);
        }

        return Promise.all(promises).then(() =>
          dispatch({
            type: 'account/GET_SUMMARY_SUCCESS',
            data,
          })
        );
      })
      .catch((err: APIError) =>
        dispatch({
          type: 'account/GET_ERROR',
          ...err,
        })
      );
  };
}

export function logIn(
  email: string | null | undefined,
  password: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, extras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/login', {
        email: email || getState().account.email,
        password,
      })
      .then((res) => res.data)
      .then((data: V2Response<V2UserSummary>) => data.result)
      .then((data) => {
        if (data.account && data.account.id) identify(data.account.id);

        // If we have meds in cart get the cart
        if (data.stats && data.stats.cartItemCount) {
          dispatch(requestCart());
        }

        dispatch({
          type: 'account/GET_SUMMARY_SUCCESS',
          data,
        });
        return data;
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}

export function logOut(redirectToHome = true): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/logout')
      .then(() => {
        if (redirectToHome) extras.history.replace('/');
        setTimeout(() => {
          document.dispatchEvent(new Event('TEARDOWN'));
        }, 100);
        return Promise.resolve();
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/LOG_OUT_ERROR',
          ...err,
        });
      });
  };
}

export function register(email: string, password?: string, skipEmail?: boolean): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    const registerEndPoint = !password ? '/v2/user/account/signup/guest' : '/v2/user/account/signup';
    return extras.blinkAPIClient
      .post(registerEndPoint, {
        email,
        password,
        skipEmail,
        acceptsAgreements: true,
        rememberMe: true,
      })
      .then(() => {
        dispatch(
          trackEvent('Created Account', {
            type: password ? 'user' : 'guest',
          })
        );
        // TODO(leo): signup should return a V2UserSummaryResponse
        return dispatch(getAccountSummary());
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}

export function getAccountType(email: string): ThunkAction<Promise<AccountType>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<AccountType> {
    return blinkAPIClient
      .post('v2/user/account/type', {
        email,
      })
      .then((res) => res.data.result)
      .then((result: { type: AccountType }) => result.type)
      .catch((e) => {
        if (e.message.includes('not found')) {
          return 'new';
        }

        throw e;
      });
  };
}

export function updateAccountState(state: AccountStateUpdate): UpdateAccountState {
  return {
    type: 'account/UPDATE_STATE',
    state,
  };
}

/* Magic Card Actions */
export function registerMagicCard(
  email: string,
  password: string,
  matchCode: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/magic_card/signup', {
        email,
        password,
        acceptsAgreements: true,
        rememberMe: true,
      })
      .then(async () => {
        await dispatch(
          trackEvent('Created Account', {
            type: 'user',
          })
        );
        // TODO(leo): signup should return a V2UserSummaryResponse
        await dispatch(getAccountSummary());
        if (matchCode) await dispatch(requestMagicTierAccess(matchCode));
        // New accounts will always not be a magic user until after verifying
        return {
          isMagicVerified: false,
        };
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}

export function logInMagicCard(
  email: string | null | undefined,
  password: string | null | undefined,
  matchCode?: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, extras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/login', {
        email: email || getState().account.email,
        password,
      })
      .then((res) => res.data)
      .then((data: V2Response<V2UserSummary>) => data.result)
      .then(async (data) => {
        if (data.account && data.account.id) identify(data.account.id);

        // Granting access on login if there is a match code
        if (matchCode) {
          await dispatch(requestMagicTierAccess(matchCode));
        }

        // If we have meds in cart get the cart
        if (data.stats && data.stats.cartItemCount) {
          dispatch(requestCart());
        }

        dispatch({
          type: 'account/GET_SUMMARY_SUCCESS',
          data,
        });
        return data;
      });
  };
}

export function verifyMagicCard(data: MatchMagicFields): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .patch('/v2/user/account/magic_card/match', data)
      .then((res) => {
        return dispatch(getAccountSummary()).then(() => res.data.result);
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}

/**
 * Assumes guests registration attempt, if unsuccsessful checks accout type
 * Desired behavior
 * Account type = user, show password field
 * Account type = guest, send magic link
 */
export function attemptGuestAuth(email: string): ThunkAction<Promise<any>> {
  return async (dispatch, getState, { blinkAPIClient }): Promise<any> => {
    try {
      return await dispatch(register(email));
    } catch (e) {
      if (e.status === 409 && e.validationErrors.email === 'Email already in use') {
        const resp = await blinkAPIClient.post('v2/user/account/type', {
          email,
        });
        dispatch(
          updateAccountState({
            email,
            type: resp.data.result.type,
          })
        );
        return resp.data.result;
      } else {
        throw e;
      }
    }
  };
}

export function updateAccount(data: UpdateAccountFields): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .patch('/v2/user/account', data)
      .then((res) => res.data)
      .then((res: V2Response<V2Account>) => res.result)
      .then((res) => {
        if (res.id) identify(res.id);
        let shouldPullAccountSummary = true;

        if (res) {
          // check if user's profile contains complete info to trigger backend rigid match
          for (const key of MAGIC_CLAIM_RIGID_MATCH_FIELD_LIST) {
            if (!res[key]) {
              shouldPullAccountSummary = false;
              break;
            }
          }

          if (shouldPullAccountSummary) {
            // get account summary because those fields change could enroll user in new pricing tier access
            for (const key of Object.keys(data)) {
              if (MAGIC_CLAIM_RIGID_MATCH_FIELD_LIST.includes(key) && !!data[key]) {
                dispatch(getAccountSummary());
                break;
              }
            }
          }
        }

        return dispatch({
          type: 'account/GET_SUCCESS',
          data: res,
        });
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/UPDATE_ERROR',
          ...err,
        });
      });
  };
}

export function setStoreTelemedCurrentState(): SetUserCurrentState {
  const data = IS_BROWSER ? localStorage.getItem('telemedCurrentState') : null;
  return {
    type: 'account/SET_USER_CURRENT_STATE',
    data,
  };
}

// this updates the users current state for zipnosis purposes.
export function saveUserCurrentState(stateAbbreviation: string): ThunkAction {
  return function (dispatch): void {
    IS_BROWSER && localStorage.setItem('telemedCurrentState', stateAbbreviation);
    dispatch(setStoreTelemedCurrentState());
  };
}

export function sendPasswordReset(email: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient.post(`/v2/user/account/password`, {
      email,
    });
  };
}

export function sendMagicLink(email: string, flow?: 'nimbus' | 'default'): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient.put(`/v2/user/account/login/guest`, {
      email,
      flow: flow || 'default',
    });
  };
}

export function createPassword(data: {
  password: string;
  confirmPassword: string;
  token: string;
}): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient.put(`/v2/user/account/password`, data);
  };
}

export function changePassword(data: { oldPassword: string; newPassword: string }): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient
      .patch(`/v2/user/account/password`, data)
      .then((res) => res.data)
      .then((data) => data.result)
      .then((result) => {
        return dispatch({
          type: 'account/CHANGE_PASSWORD_SUCCESS',
          data: result,
        });
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/CHANGE_PASSWORD_ERROR',
          ...err,
        });
      });
  };
}

export function guestLogin(data: { token: string }): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient.post(`/v2/user/account/login/guest`, data);
  };
}

export function getAccountAddress(shippingDefault = false): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient
      .get(`/v2/user/account/address?shipping_default=${String(shippingDefault)}`)
      .then((res) => res.data)
      .then((data) => data.result)
      .then((result) => {
        return dispatch({
          type: 'account/GET_ADDRESS_SUCCESS',
          data: result,
        });
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/GET_ADDRESS_ERROR',
          ...err,
        });
      });
  };
}

export function setAccountAddress(address: AccountAddress | MaybeAccountAddress): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient
      .post('/v2/user/account/address', address)
      .then((res) => res.data)
      .then((data) => data.result)
      .then((result) =>
        dispatch({
          type: 'account/GET_ADDRESS_SUCCESS',
          data: result,
        })
      )
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/GET_ADDRESS_ERROR',
          ...err,
        });
      });
  };
}

export function getUserLocation(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    // Gets user location from account info
    // - From account delivery address
    // - From magic pharmacy address if this is a magic card account
    return blinkAPIClient
      .get('/v2/user/location')
      .then((res) => dispatch(setUserLocation(res.data.result)))
      .catch(() => dispatch(inferUserLocation()));
  };
}

export function inferUserLocation(lat?: number, lon?: number): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    // Infers user location by location data
    // - by (Lattitude, Longitude) if provided
    // - falls back to IP to location if coordinates not provided
    let url = '/v2/user/location/infer';

    if (lat && lon) {
      url += `?lat=${lat}&lon=${lon}`;
    }

    return blinkAPIClient
      .get(url)
      .then((res) => dispatch(setUserLocation(res.data.result)))
      .catch(() => {
        const error = 'Failed to infer location';
        dispatch(
          setUserLocation({
            city: null,
            stateCode: null,
            zipCode: null,
            healthSystems: null,
            error,
          })
        );
        return {
          error,
        };
      });
  };
}

export function setUserLocation(location: UserLocationType): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch): Promise<any> {
    return Promise.resolve(
      dispatch({
        type: 'user/SET_USER_LOCATION_SUCCESS',
        data: location,
      })
    ).then(() => {
      setUserLocationToSessionStorage({
        location,
      });
      return location;
    });
  };
}

export function searchLocation(zipCode: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    return blinkAPIClient
      .get(`/v2/user/location/search?zip_code=${zipCode}`)
      .then((res) => res.data)
      .then((data) => data.result)
      .catch((err: APIError) => {
        return {
          error: err.message,
        };
      });
  };
}

type AccountAndAddressUpdate = {
  accountUpdate: UpdateAccountFields;
  addressUpdate: MaybeAccountAddress;
};
type AccountAndAddressUpdateResult = {
  code: {
    NO_ERROR: boolean;
    ACCOUNT_ERROR: boolean;
    ADDRESS_ERROR: boolean;
    ADDRESS_SUGGESTION: boolean;
  };
  address: {
    error: string | null | undefined;
    suggestedAddresses: Array<MaybeAccountAddress>;
  };
  account: {
    error: string | null | undefined;
  };
};

export function updateAccountAndAddress(
  update: AccountAndAddressUpdate
): ThunkAction<Promise<AccountAndAddressUpdateResult>> {
  return async function (dispatch: Dispatch): Promise<AccountAndAddressUpdateResult> {
    const output = {
      address: {
        error: null,
        suggestedAddresses: [],
      },
      account: {
        error: null,
      },
      code: {
        NO_ERROR: false,
        ACCOUNT_ERROR: false,
        ADDRESS_ERROR: false,
        ADDRESS_SUGGESTION: false,
      },
    };
    const { accountUpdate, addressUpdate } = update;
    // Conversion from `MaybeAccountAddress` to `AccountAddress`
    const { address1 = '', address2, city = '', state = '', zipCode = '' } = addressUpdate;
    const [addressRes, accountRes] = await Promise.all([
      dispatch(
        updateDeliveryAddress({
          address1,
          address2,
          city,
          state,
          zipCode,
        })
      ),
      dispatch(updateAccount(accountUpdate)),
    ]);
    let hasAnyErrors = false;
    const addressError =
      addressRes && addressRes.error && addressRes.validationErrors && addressRes.validationErrors.shippingAddress;
    const accountError =
      accountRes && accountRes.error && accountRes.validationErrors && accountRes.validationErrors.mobileNumber;

    if (addressRes.status === 409) {
      output.code.ADDRESS_SUGGESTION = hasAnyErrors = true;
      output.address.suggestedAddresses = addressRes.results;
    } else if (addressError) {
      output.code.ADDRESS_ERROR = hasAnyErrors = true;
      output.address.error = addressError;
    }

    if (accountError) {
      output.code.ACCOUNT_ERROR = hasAnyErrors = true;
      output.account.error = accountError;
    }

    output.code.NO_ERROR = !hasAnyErrors;
    return output;
  };
}

export function formatDOB(dob: string) {
  const [month, day, year] = dob.split('/');
  return {
    month: month,
    day: day,
    year: year,
  };
}

export function formatGender(gender: string) {
  const lowercasedGender = gender.toLowerCase();
  return ['m', 'male'].indexOf(lowercasedGender) !== -1 ? 'male' : 'female';
}

/**
 * Determines where the user should go in the checkout assistant
 *   replace: use history.replace(route) rather than history.push(route)
 *  users will be directed from the mdv straight to the cart execpt for if any telemed drugs are in cart
 */
export function navigateToCheckoutStep(replace?: boolean, toCart?: boolean): ThunkAction {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): void {
    if (toCart) return history.push('/cart');
    const route = getCheckoutRouteFromState(getState());
    return replace ? history.replace(route) : history.push(route);
  };
}

type UserStats = {
  isLoggedIn: boolean;
  hasCompletedProfile: boolean;
  hasGivenMobilePhone: boolean;
  isBuyingConsultations: boolean;
  hasSelectedPharmacy: boolean;
  isDelivery: boolean;
  cartHasTelemedItems: boolean;
  hasConfirmedPrescriptionsForRetail: boolean;
  hasConfirmedPrescriptionsForDelivery: boolean;
  hasVerifiedDeliverySafeAddress: boolean;
  isTelemedSafeAddress: boolean;
  hasPatientProfile: boolean;
  hasPrescriptionSource: boolean;
  hasCards: boolean;
  isSubsEligible: boolean | null | undefined;
  isOTPAccount: boolean;
  hasEmailAddress: boolean;
};

/**
 * ! Warning: this is going to be deprecated after Sunset checkout will be released
 */
function getUserStats(state: AppState): UserStats {
  const { account, session, pharmacy, cart, delivery, paymentMethods } = state;
  const isSubsEligible = isEligible(state.subscriptions);
  const isLoggedIn = session.authenticated;
  const hasCompletedProfile = Boolean(account.firstName && account.lastName && account.dob && account.gender);
  const hasGivenMobilePhone = !!account.mobileNumber;
  const hasSelectedPharmacy = !!pharmacy.selectedPharmacy;
  const isDelivery = Boolean(pharmacy.selectedPharmacy && pharmacy.selectedPharmacy.isDelivery);
  // Telemed/Prescription Checks
  // Prescription confirmation is when the user confirms Yes/No for having a prescription for a cart drug
  // It is always needed for delivery orders
  // It is only needed for retail/pickup orders if there are telemed drugs
  // When users answer 'No' for a telemed drug, that will lead to a telemed order and is tracked in isBuyingConsultations
  const confirmedPrescriptions = (cart && cart.confirmedPrescriptions) || [];
  const allPrescriptionsConfirmed = cart && cart.items.every((item) => confirmedPrescriptions.includes(item.medId));
  const cartHasTelemedItems = cart && hasTelemedDrugs(cart.items);
  // ** The user says they don't have an Rx for a telemed drug, and are therefore buying a consultation
  const isBuyingConsultations = cart && (cart.isBuyingConsultations || []).length > 0;
  // ** For delivery, everything needs to be confirmed
  const hasConfirmedPrescriptionsForDelivery = allPrescriptionsConfirmed;
  // ** For retail, everything only needs to be confirmed if there are telemed eligibile drugs
  const hasConfirmedPrescriptionsForRetail = !cartHasTelemedItems || allPrescriptionsConfirmed;
  const hasVerifiedDeliverySafeAddress = Boolean(
    account.hasShippingAddress &&
      delivery.shippingAddress &&
      addressIsDeliverySafe(delivery.shippingAddress.state) &&
      account.mobileNumber
  );
  const isTelemedSafeAddress = Boolean(
    account.hasShippingAddress && delivery.shippingAddress && addressIsDeliverySafe(delivery.shippingAddress.state)
  );
  const hasPatientProfile = Boolean(account.hasPatientProfile);
  const hasPrescriptionSource =
    cart &&
    cart.items.every(
      (cartItem) =>
        cartItem.rxSrcProviderId ||
        cartItem.rxSrcPharmacyId ||
        cartItem.rxSrcType === 'user' ||
        cartItem.rxSrcType === 'consultation'
    );
  const hasCards = account.paymentCardCount > 0 || (paymentMethods && paymentMethods.length > 0);
  const isOTPAccount = account.type === 'sms_otp';
  const hasEmailAddress = Boolean(account.email);
  return {
    isLoggedIn,
    hasCompletedProfile,
    hasGivenMobilePhone,
    isBuyingConsultations,
    hasSelectedPharmacy,
    isDelivery,
    cartHasTelemedItems,
    hasConfirmedPrescriptionsForRetail,
    hasConfirmedPrescriptionsForDelivery,
    hasVerifiedDeliverySafeAddress,
    isTelemedSafeAddress,
    hasPatientProfile,
    hasPrescriptionSource,
    hasCards,
    isSubsEligible,
    hasEmailAddress,
    isOTPAccount,
  };
}

export function getIsAccountInfoEditable(state: AppState) {
  return state.account.paymentCardCount === 0 && !getIsOTPAccount(state);
}

export function getIsOTPAccount(state: AppState) {
  return state.account && state.account.type === 'sms_otp';
}

export function getMedsConditionId(state: AppState) {
  const med = subsCartMed(state);
  return (med && med.conditionId) || null;
}

export function getMedName(state: AppState) {
  const med = subsCartMed(state);
  return (med && med.formattedName) || null;
}

export function getMedNameId(state: AppState) {
  const med = subsCartMed(state);
  return (med && med.medNameId) || null;
}

function subsCartMed(state: AppState) {
  return state.subscriptions.cart && state.subscriptions.cart.med;
}

/**
 * ! Warning: this is going to be deprecated after Sunset checkout will be released
 */
export function getCheckoutRouteFromState(state: AppState): string {
  const {
    isLoggedIn,
    hasCompletedProfile,
    hasGivenMobilePhone,
    isBuyingConsultations,
    isTelemedSafeAddress,
    isDelivery,
    hasConfirmedPrescriptionsForRetail,
    isOTPAccount,
    hasEmailAddress,
  } = getUserStats(state);
  // Skipping /start/4 for magic users
  const hasMagicAccess = getHasMagicAccess(state);
  const skipPrescriptionsCheck =
    hasMagicAccess || hasConfirmedPrescriptionsForRetail || (isOTPAccount && !hasEmailAddress);
  //If the user is buying a consultation, we need an address (for clinical partner)
  const hasGivenAddressForTelemed = isBuyingConsultations ? isTelemedSafeAddress : true;
  if (isDelivery) return getDeliveryRouteFromState(state);

  if (
    isLoggedIn &&
    hasCompletedProfile &&
    skipPrescriptionsCheck &&
    hasEmailAddress &&
    hasGivenAddressForTelemed &&
    hasGivenMobilePhone
  ) {
    return '/checkout/review-order';
  }

  if (isLoggedIn && hasCompletedProfile && skipPrescriptionsCheck) return '/start/5';
  if (isLoggedIn && hasCompletedProfile) return '/start/4';
  if (isLoggedIn) return '/start/3';
  return '/start/2';
}

/**
 * ! Warning: this is going to be deprecated after Sunset checkout will be released
 */
function getDeliveryRouteFromState(state: AppState): string {
  const {
    isLoggedIn,
    hasCompletedProfile,
    hasConfirmedPrescriptionsForDelivery,
    hasVerifiedDeliverySafeAddress,
    hasPatientProfile,
    hasPrescriptionSource,
    hasCards,
  } = getUserStats(state);
  const userHasAllRefills = getHasAllRefills(state);
  if (
    isLoggedIn &&
    ((hasCompletedProfile &&
      hasVerifiedDeliverySafeAddress &&
      hasPatientProfile &&
      hasCards &&
      hasPrescriptionSource &&
      hasConfirmedPrescriptionsForDelivery) ||
      userHasAllRefills)
  )
    return '/checkout/review-order';
  if (
    isLoggedIn &&
    hasCompletedProfile &&
    hasPrescriptionSource &&
    hasVerifiedDeliverySafeAddress &&
    hasPatientProfile &&
    hasConfirmedPrescriptionsForDelivery
  )
    return '/delivery/7';
  if (
    isLoggedIn &&
    hasCompletedProfile &&
    hasPrescriptionSource &&
    hasVerifiedDeliverySafeAddress &&
    hasConfirmedPrescriptionsForDelivery
  )
    return '/delivery/6';
  if (isLoggedIn && hasCompletedProfile && hasPrescriptionSource && hasConfirmedPrescriptionsForDelivery)
    return '/delivery/5';
  if (isLoggedIn && hasCompletedProfile && hasConfirmedPrescriptionsForDelivery) return '/delivery/4';
  if (isLoggedIn && hasCompletedProfile) return '/delivery/3';
  if (isLoggedIn) return '/delivery/2';
  return '/delivery/1';
}

export function navigateToCheckoutStepV2({
  isSubscription,
  toCart,
}: {
  isSubscription?: boolean;
  toCart?: boolean;
} = {}): ThunkAction {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): void {
    if (toCart) {
      return history.push('/cart');
    }

    const userStats = getUserStatsV2(getState());

    if (isSubscription || userStats.isSubscription) {
      return history.push(getCheckoutSubscriptionRoute(userStats));
    }

    if (userStats.isDelivery) {
      return history.push(getCheckoutDeliveryRoute(userStats));
    }

    return history.push(getCheckoutPickupRoute(userStats));
  };
}

export function getCheckoutStepState(
  currentPath: string,
  {
    isDelivery,
    isSubscription,
  }: {
    isDelivery: boolean;
    isSubscription: boolean;
  }
): any {
  const steps = isSubscription
    ? CHECKOUT_ROUTES_SUBSCRIPTION
    : isDelivery
    ? CHECKOUT_ROUTES_DELIVERY
    : CHECKOUT_ROUTES_PICKUP;
  const step = Object.values(steps).findIndex((path) => path === currentPath) + 1;
  const totalSteps = Object.keys(steps).length;
  return {
    step,
    steps,
    totalSteps,
  };
}

export function getCheckoutPickupRoute({
  isLoggedIn,
  hasCompletedProfile,
  hasGivenMobilePhone,
  isBuyingConsultations,
  isTelemedSafeAddress,
  hasConfirmedPrescriptionsForRetail,
  hasSelectedPharmacy,
  isOTPAccount,
  hasEmailAddress,
  hasCards,
  hasMagicAccess,
}: UserStatsV2): string {
  const skipPrescriptionsCheck =
    hasMagicAccess || hasConfirmedPrescriptionsForRetail || (isOTPAccount && !hasEmailAddress);
  //If the user is buying a consultation, we need an address (for clinical partner)
  const hasGivenAddressForTelemed = isBuyingConsultations ? isTelemedSafeAddress : true;

  if (
    isLoggedIn &&
    hasCompletedProfile &&
    skipPrescriptionsCheck &&
    hasSelectedPharmacy &&
    hasEmailAddress &&
    hasGivenAddressForTelemed &&
    hasGivenMobilePhone &&
    hasCards
  ) {
    return '/checkout/review-order';
  }

  if (
    isLoggedIn &&
    hasCompletedProfile &&
    skipPrescriptionsCheck &&
    hasSelectedPharmacy &&
    hasEmailAddress &&
    hasGivenAddressForTelemed &&
    hasGivenMobilePhone
  )
    return CHECKOUT_ROUTES_PICKUP.PAYMENT_INFO;
  if (
    isLoggedIn &&
    hasCompletedProfile &&
    skipPrescriptionsCheck &&
    hasSelectedPharmacy &&
    hasEmailAddress &&
    hasGivenMobilePhone
  )
    return CHECKOUT_ROUTES_PICKUP.TELEHEALTH_ADDRESS;
  if (isLoggedIn && hasCompletedProfile && skipPrescriptionsCheck && hasSelectedPharmacy)
    return CHECKOUT_ROUTES_PICKUP.COMMUNICATION_OPT_IN;
  if (isLoggedIn && hasCompletedProfile && skipPrescriptionsCheck) return CHECKOUT_ROUTES_PICKUP.SELECT_PHARMACY;
  if (isLoggedIn && hasCompletedProfile) return CHECKOUT_ROUTES_PICKUP.PRESCRIPTION_CONFIRMATION;
  if (isLoggedIn) return CHECKOUT_ROUTES_PICKUP.BASIC_INFO;
  return CHECKOUT_ROUTES_PICKUP.LOGIN_OR_SIGNUP;
}

function getCheckoutDeliveryRoute({
  isLoggedIn,
  hasCompletedProfile,
  hasConfirmedPrescriptionsForDelivery,
  hasPrescriptionSource,
  hasVerifiedDeliverySafeAddress,
  hasPatientProfile,
  hasCards,
  hasGivenMobilePhone,
  hasEmailAddress,
  hasAllRefills,
  cartHasAutoRefillItems,
  shouldOmitPaymentStep,
}: UserStatsV2): string {
  if (
    isLoggedIn &&
    ((hasCompletedProfile &&
      hasConfirmedPrescriptionsForDelivery &&
      hasPrescriptionSource &&
      hasVerifiedDeliverySafeAddress &&
      hasPatientProfile &&
      hasCards &&
      hasGivenMobilePhone &&
      hasEmailAddress) ||
      hasAllRefills)
  ) {
    if (cartHasAutoRefillItems) {
      return shouldOmitPaymentStep ? '/checkout/review-order' : CHECKOUT_ROUTES_DELIVERY.PAYMENT_INFO;
    }

    return '/checkout/review-order';
  }

  if (
    isLoggedIn &&
    hasCompletedProfile &&
    hasConfirmedPrescriptionsForDelivery &&
    hasPrescriptionSource &&
    hasVerifiedDeliverySafeAddress &&
    hasPatientProfile &&
    hasGivenMobilePhone
  )
    return CHECKOUT_ROUTES_DELIVERY.PAYMENT_INFO;
  if (
    isLoggedIn &&
    hasCompletedProfile &&
    hasConfirmedPrescriptionsForDelivery &&
    hasPrescriptionSource &&
    hasVerifiedDeliverySafeAddress &&
    hasPatientProfile
  )
    return CHECKOUT_ROUTES_DELIVERY.COMMUNICATION_OPT_IN;
  if (
    isLoggedIn &&
    hasCompletedProfile &&
    hasConfirmedPrescriptionsForDelivery &&
    hasPrescriptionSource &&
    hasVerifiedDeliverySafeAddress
  )
    return CHECKOUT_ROUTES_DELIVERY.ADDITIONAL_INFO;
  if (isLoggedIn && hasCompletedProfile && hasConfirmedPrescriptionsForDelivery && hasPrescriptionSource)
    return CHECKOUT_ROUTES_DELIVERY.DELIVERY_ADDRESS;
  if (isLoggedIn && hasCompletedProfile && hasConfirmedPrescriptionsForDelivery)
    return CHECKOUT_ROUTES_DELIVERY.RX_TRANSFER;
  if (isLoggedIn && hasCompletedProfile) return CHECKOUT_ROUTES_DELIVERY.PRESCRIPTION_CONFIRMATION;
  if (isLoggedIn) return CHECKOUT_ROUTES_DELIVERY.BASIC_INFO;
  return CHECKOUT_ROUTES_DELIVERY.LOGIN_OR_SIGNUP;
}

function getCheckoutSubscriptionRoute({
  isLoggedIn,
  hasVerifiedDeliverySafeAddress,
  isSubsEligible,
  hasPatientProfile,
  hasGivenMobilePhone,
  hasCards,
}: UserStatsV2): string {
  if (
    isLoggedIn &&
    isSubsEligible &&
    hasVerifiedDeliverySafeAddress &&
    hasPatientProfile &&
    hasGivenMobilePhone &&
    hasCards
  )
    return '/checkout/subscriptions/review-order';
  if (isLoggedIn && isSubsEligible && hasVerifiedDeliverySafeAddress && hasPatientProfile && hasGivenMobilePhone)
    return CHECKOUT_ROUTES_SUBSCRIPTION.PAYMENT_INFO;
  if (isLoggedIn && isSubsEligible && hasVerifiedDeliverySafeAddress && hasPatientProfile)
    return CHECKOUT_ROUTES_SUBSCRIPTION.COMMUNICATION_OPT_IN;
  if (isLoggedIn && isSubsEligible && hasVerifiedDeliverySafeAddress)
    return CHECKOUT_ROUTES_SUBSCRIPTION.ADDITIONAL_INFO;
  if (isLoggedIn && isSubsEligible) return CHECKOUT_ROUTES_SUBSCRIPTION.DELIVERY_ADDRESS;
  if (isLoggedIn) return CHECKOUT_ROUTES_SUBSCRIPTION.BASIC_INFO;
  return CHECKOUT_ROUTES_SUBSCRIPTION.LOGIN_OR_SIGNUP;
}

type UserStatsV2 = {
  isLoggedIn: boolean;
  hasCompletedProfile: boolean;
  hasGivenMobilePhone: boolean;
  isBuyingConsultations: boolean;
  hasSelectedPharmacy: boolean;
  isDelivery: boolean;
  isSubscription: boolean;
  cartHasTelemedItems: boolean;
  cartHasAutoRefillItems: boolean;
  hasConfirmedPrescriptionsForRetail: boolean;
  hasConfirmedPrescriptionsForDelivery: boolean;
  hasVerifiedDeliverySafeAddress: boolean;
  isTelemedSafeAddress: boolean;
  hasPatientProfile: boolean;
  hasPrescriptionSource: boolean;
  hasCards: boolean;
  isSubsEligible: boolean | null | undefined;
  isOTPAccount: boolean;
  hasEmailAddress: boolean;
  hasMagicAccess: boolean;
  hasAllRefills: boolean;
  shouldOmitPaymentStep: boolean;
};

function getUserStatsV2(state: AppState): UserStatsV2 {
  const { account, header, session, pharmacy, cart, delivery, paymentMethods } = state;
  const isSubsEligible = isEligible(state.subscriptions);
  const isLoggedIn = session.authenticated;
  const hasCompletedProfile = Boolean(account.firstName && account.lastName && account.dob && account.gender);
  const hasGivenMobilePhone = !!account.mobileNumber;
  const hasSelectedPharmacy = !!pharmacy.selectedPharmacy && !pharmacy.selectedPharmacy.isDelivery;
  const isDelivery = cart && cart.items && cart.items.some((item) => item.priceCategory === 'delivery');
  const isSubscription = header.currPath.includes(CHECKOUT_SUBSCRIPTIONS_BASE_PATH);
  // Telemed/Prescription Checks
  // Prescription confirmation is when the user confirms Yes/No for having a prescription for a cart drug
  // It is always needed for delivery orders
  // It is only needed for retail/pickup orders if there are telemed drugs
  // When users answer 'No' for a telemed drug, that will lead to a telemed order and is tracked in isBuyingConsultations
  const confirmedPrescriptions = (cart && cart.confirmedPrescriptions) || [];
  const allPrescriptionsConfirmed = cart && cart.items.every((item) => confirmedPrescriptions.includes(item.medId));
  const cartHasTelemedItems = cart && hasTelemedDrugs(cart.items);
  const scheduledMedNameIds = getScheduledMedNameIds(state);
  const autoRefillEligibleMeds = cart && getAutoRefillEligibleMeds(cart.items, scheduledMedNameIds);
  const cartHasAutoRefillItems = Boolean(autoRefillEligibleMeds.length);
  // ** The user says they don't have an Rx for a telemed drug, and are therefore buying a consultation
  const isBuyingConsultations = cart && (cart.isBuyingConsultations || []).length > 0;
  // ** For delivery, everything needs to be confirmed
  const hasConfirmedPrescriptionsForDelivery = allPrescriptionsConfirmed;
  // ** For retail, everything only needs to be confirmed if there are telemed eligibile drugs
  const hasConfirmedPrescriptionsForRetail = !cartHasTelemedItems || allPrescriptionsConfirmed;
  const hasVerifiedDeliverySafeAddress = Boolean(
    account.hasShippingAddress && delivery.shippingAddress && addressIsDeliverySafe(delivery.shippingAddress.state)
  );
  const isTelemedSafeAddress = Boolean(
    account.hasShippingAddress && delivery.shippingAddress && addressIsDeliverySafe(delivery.shippingAddress.state)
  );
  const hasPatientProfile = Boolean(account.hasPatientProfile);
  const hasPrescriptionSource =
    cart &&
    cart.items.every(
      (cartItem) =>
        cartItem.rxSrcProviderId ||
        cartItem.rxSrcPharmacyId ||
        cartItem.rxSrcType === 'user' ||
        cartItem.rxSrcType === 'consultation'
    );
  const hasCards = account.paymentCardCount > 0 || (paymentMethods && paymentMethods.length > 0);
  const isOTPAccount = account.type === 'sms_otp';
  const hasEmailAddress = Boolean(account.email);
  const hasMagicAccess = getHasMagicAccess(state);
  const hasAllRefills = getHasAllRefills(state);
  const wasOnPaymentOrPostPaymentStep = [CHECKOUT_STEPS.PAYMENT_INFO, CHECKOUT_STEPS.COMMUNICATION_OPT_IN].some(
    (stepPath) => state.header.currPath.includes(stepPath)
  );
  const wasOnEditingStep = Boolean(header.currPathState && header.currPathState.isEditing);
  const shouldOmitPaymentStep = wasOnPaymentOrPostPaymentStep || wasOnEditingStep;
  return {
    isLoggedIn,
    hasCompletedProfile,
    hasGivenMobilePhone,
    isBuyingConsultations,
    hasSelectedPharmacy,
    isDelivery,
    isSubscription,
    cartHasTelemedItems,
    cartHasAutoRefillItems,
    hasConfirmedPrescriptionsForRetail,
    hasConfirmedPrescriptionsForDelivery,
    hasVerifiedDeliverySafeAddress,
    isTelemedSafeAddress,
    hasPatientProfile,
    hasPrescriptionSource,
    hasCards,
    isSubsEligible,
    hasEmailAddress,
    isOTPAccount,
    hasMagicAccess,
    hasAllRefills,
    shouldOmitPaymentStep,
  };
}

export function markReverieUser(exitToken: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }): Promise<any> {
    const platform = isDesktop(getState()) ? 'desktop' : 'mobile';
    return blinkAPIClient
      .put(`/reverie/api/map_exit_token?onboarding_source=${platform}`, {
        exitToken,
      })
      .then(() => dispatch(markReverieUserSuccess()));
  };
}

function markReverieUserSuccess(): MarkReverieUserSuccess {
  return {
    type: 'account/MARK_REVERIE_USER_SUCCESS',
  };
}

export function getMagicCoupon(couponName?: string | null | undefined): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post('/v2/user/account/magic_card/coupon', {
        couponName,
      })
      .then((res) => res.data)
      .then((data) => dispatch(receiveMagicCoupon(data.result)));
  };
}

function receiveMagicCoupon(magicCoupon): ReceiveMagicCoupon {
  return {
    type: 'account/RECEIVE_MAGIC_COUPON',
    magicCoupon,
  };
}

/**
 * Selectors
 */
export const getBannerMessage = (state: AppState, ownProps: RouteComponentProps): string | null | undefined => {
  const { hasMagicCoupon, hasReferralCoupon, orderCount, userLocation, magicCoupon } = state.account;
  let bannerMessage;
  const isMagicUser = getUserMagicTier(state);
  const selectedDrug: Drug | null | undefined = getDrug(state.drugs);
  const isTelehealth = selectedDrug && selectedDrug.hasTelemedMedIdEligibility;
  const isOpioidDrug = isOpioid((selectedDrug && selectedDrug.medNameId) || '');
  const currentUrl = ownProps.match.url;

  if (currentUrl !== '/') {
    // do not show banner on mdv
    if (isTelehealth || isOpioidDrug) return null;
  }

  const onMyMedsPage = currentUrl === '/account/medications';
  const hasOrders = orderCount > 0;

  if (selectedDrug && selectedDrug.pharmaPartnerDetails && currentUrl !== '/') {
    bannerMessage = selectedDrug.pharmaPartnerDetails.bannerText;
  } else if (hasMagicCoupon && magicCoupon) {
    bannerMessage = magicCoupon.bannerMessage;
  } else if ((!onMyMedsPage && hasReferralCoupon) || state.notifications.bannerText) {
    bannerMessage = state.notifications.bannerText
      ? state.notifications.bannerText
      : REFERRAL_FIRST_FILL_COUPON_MESSAGE;
  } else if (userLocation && userLocation.stateCode === 'TX' && !isMagicUser) {
    bannerMessage = PRIVIA_BANNER_MESSAGE;
  } else if (!onMyMedsPage && (!state.session.authenticated || !hasOrders)) {
    bannerMessage = NON_REFERRAL_FIRST_FILL_COUPON_MESSAGE;
  }

  return bannerMessage;
};
export const getIsMagicVerified = (state: AppState) => state.account && state.account.isMagicVerified;

/**
 * Util
 */
function mergeResponse(state: AccountState, data: V2Account): AccountState {
  return {
    ...state,
    dob: data.dob,
    email: data.email,
    firstName: data.firstName,
    gender: data.gender,
    id: data.id,
    lastName: data.lastName,
    mobileNumber: data.mobileNumber,
    prescriptions: data.prescriptions,
    smsOptIn: data.smsOptIn,
    medInfoOptIn: data.medInfoOptIn,
  };
}

/**
 * reducer
 */
export const initialState: AccountState = {
  dob: null,
  email: null,
  firstName: null,
  gender: null,
  hasConsultations: false,
  hasMagicCoupon: false,
  hasPatientProfile: false,
  hasReferralCoupon: false,
  hasShippingAddress: false,
  hasSubscriptions: false,
  id: null,
  isMagicVerified: false,
  isReverieUser: false,
  lastName: null,
  medInfoOptIn: false,
  mobileNumber: null,
  orderCount: 0,
  paymentCardCount: 0,
  prescriptions: [],
  sessionId: null,
  smsOptIn: false,
  type: null,
  telemedCurrentState: null,
  magicCoupon: null,
};

export default function accountReducer(state: AccountState = { ...initialState }, action: AppAction): AccountState {
  switch (action.type) {
    case 'delivery/SET_PROFILE_SUCCESS': {
      return { ...state, hasPatientProfile: true };
    }

    case 'account/GET_ADDRESS_SUCCESS': {
      const shippingAddress = action.data;
      return { ...state, hasShippingAddress: shippingAddress && shippingAddress.isVerified };
    }

    case 'account/UPDATE_STATE': {
      return { ...state, ...action.state };
    }

    case 'account/GET_SUCCESS': {
      return mergeResponse(state, action.data);
    }

    case 'account/SET_USER_CURRENT_STATE': {
      return { ...state, telemedCurrentState: action.data };
    }

    case 'account/GET_SUMMARY_SUCCESS': {
      const a: V2Account = action.data?.account;
      const stats: V2AccountStats = action.data?.stats;
      const sessionId = action.data?.session?.id;
      const paymentCardCount = action.data?.stats?.paymentCardCount;
      const telemedCurrentStateInStorage = IS_BROWSER && localStorage.getItem('telemedCurrentState');
      const magicCoupon = action.data?.magicCoupon;
      return {
        ...state,
        id: a?.id || '',
        firstName: a?.firstName || '',
        lastName: a?.lastName || '',
        gender: a?.gender || null,
        dob: a?.dob || '',
        email: a?.email || '',
        mobileNumber: a?.mobileNumber || '',
        orderCount: stats?.orderCount || 0,
        sessionId: sessionId,
        type: a?.type,
        prescriptions: a?.prescriptions || [],
        paymentCardCount: paymentCardCount || 0,
        hasPatientProfile: a?.hasPatientProfile,
        smsOptIn: a?.smsOptIn || false,
        medInfoOptIn: a?.medInfoOptIn || false,
        hasShippingAddress: stats?.hasShippingAddress || false,
        hasSubscriptions: stats?.hasSubscription,
        hasConsultations: stats?.hasConsultation,
        hasReferralCoupon: stats?.hasReferralCoupon,
        hasMagicCoupon: stats?.hasMagicCoupon || false,
        isReverieUser: action.data.isReverieUser,
        isMagicVerified: action.data.isMagicVerified || false,
        telemedCurrentState: telemedCurrentStateInStorage || null,
        magicCoupon: magicCoupon || null,
      };
    }

    case 'account/RECEIVE_MAGIC_COUPON':
      return { ...state, magicCoupon: action.magicCoupon, hasMagicCoupon: true };

    case 'account/MARK_REVERIE_USER_SUCCESS':
      return { ...state, isReverieUser: true };

    case 'user/SET_USER_LOCATION_SUCCESS':
      return { ...state, userLocation: action.data };

    default:
      return state;
  }
}
